import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './login.css';
import { GRADIENTS } from 'utils/theme';
import LoadingSpinner from 'components/ui/LoadingSpinner';
import Useaxios from 'utils/Useaxios';
import { API } from 'utils/Apiroutes';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const { fetchData, loading } = Useaxios();
    const [toasts, setToasts] = useState([]);

    const showToast = (message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 3000);
    };

    const showSuccess = (message) => showToast(message, 'success');
    const showError = (message) => showToast(message, 'error');
    
    const [step, setStep] = useState(1); // 1: email, 2: otp, 3: new password
    const [formData, setFormData] = useState({
        email: localStorage.getItem('userEmail') || '',
        otp: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [otpSent, setOtpSent] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateEmail = () => {
        const newErrors = {};
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateOtp = () => {
        const newErrors = {};
        if (!formData.otp.trim()) {
            newErrors.otp = 'OTP is required';
        } else if (formData.otp.length !== 6) {
            newErrors.otp = 'OTP must be 6 digits';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePassword = () => {
        const newErrors = {};
        if (!formData.newPassword.trim()) {
            newErrors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 6) {
            newErrors.newPassword = 'Password must be at least 6 characters';
        }
        
        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Confirm password is required';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSendOtp = async (e) => {
        e.preventDefault();
        if (!validateEmail()) return;

        try {
            const response = await fetchData({
                url: API.sendOtp,
                method: 'post',
                data: { email: formData.email }
            });

            if (response.success) {
                showSuccess('🎉 OTP sent successfully to your email!');
                setOtpSent(true);
                setStep(2);
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to send OTP');
        }
    };

    const handleResendOtp = async () => {
        try {
            const response = await fetchData({
                url: API.sendOtp,
                method: 'post',
                data: { email: formData.email }
            });

            if (response.success) {
                showSuccess('📧 OTP resent successfully!');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to resend OTP');
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (!validateOtp()) return;
        showSuccess('✅ OTP verified successfully!');
        setStep(3);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (!validatePassword()) return;

        try {
            const response = await fetchData({
                url: API.forgetPassword,
                method: 'post',
                data: {
                    email: formData.email,
                    otp: parseInt(formData.otp),
                    newPassword: formData.newPassword
                }
            });

            if (response.success) {
                showSuccess('🔐 Password reset successfully! Redirecting to login...');
                localStorage.removeItem('userEmail');
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to reset password');
        }
    };

    const renderStep1 = () => (
        <form onSubmit={handleSendOtp} className="login-form" autoComplete="off">
            <div className="form-group" style={{ position: 'relative' }}>
                <input
                    className={`custom-input ${errors.email ? 'error-input' : ''}`}
                    style={{
                        width: '100%',
                        padding: "18px 18px",
                        borderRadius: "8px",
                        border: "none",
                        outline: "none",
                        height: "5vh",
                    }}
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    autoComplete="off"
                />
                <div className="input-focus-line"></div>
                {errors.email && <span className="input-error">{errors.email}</span>}
            </div>

            <button type="submit" className="login-button" disabled={loading}>
                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <LoadingSpinner size="small" color="white" />
                        <span>Sending OTP...</span>
                    </div>
                ) : (
                    <span className="button-text">Send OTP</span>
                )}
            </button>
        </form>
    );

    const renderStep2 = () => (
        <form onSubmit={handleVerifyOtp} className="login-form" autoComplete="off">
            {otpSent && (
                <div style={{ 
                    background: 'rgba(21, 159, 156, 0.1)', 
                    border: '1px solid rgba(21, 159, 156, 0.3)',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '20px',
                    textAlign: 'center',
                    color: '#159F9C',
                    fontSize: '14px'
                }}>
                    📧 OTP has been sent to {formData.email}
                </div>
            )}
            
            <div className="form-group" style={{ position: 'relative' }}>
                <input
                    className={`custom-input ${errors.otp ? 'error-input' : ''}`}
                    style={{
                        width: '100%',
                        padding: "18px 18px",
                        borderRadius: "8px",
                        border: "none",
                        outline: "none",
                        height: "5vh",
                        textAlign: 'center',
                        fontSize: '18px',
                        letterSpacing: '2px'
                    }}
                    type="text"
                    name="otp"
                    value={formData.otp}
                    onChange={handleChange}
                    placeholder="Enter 6-digit OTP"
                    maxLength="6"
                    autoComplete="off"
                />
                <div className="input-focus-line"></div>
                {errors.otp && <span className="input-error">{errors.otp}</span>}
            </div>

            <button type="submit" className="login-button" disabled={loading}>
                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <LoadingSpinner size="small" color="white" />
                        <span>Verifying...</span>
                    </div>
                ) : (
                    <span className="button-text">Verify OTP</span>
                )}
            </button>

            <div style={{ textAlign: 'center', marginTop: '15px' }}>
                <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#159F9C',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontSize: '14px'
                    }}
                >
                    Resend OTP
                </button>
            </div>
        </form>
    );

    const renderStep3 = () => (
        <form onSubmit={handleResetPassword} className="login-form" autoComplete="off">
            <div className="form-group" style={{ position: 'relative' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                        className={`custom-input ${errors.newPassword ? 'error-input' : ''}`}
                        style={{
                            width: '100%',
                            padding: "18px 50px 18px 18px",
                            borderRadius: "8px",
                            border: "none",
                            outline: "none",
                            height: "5vh",
                        }}
                        type={showPassword ? "text" : "password"}
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleChange}
                        placeholder="New Password"
                        minLength="6"
                        autoComplete="new-password"
                    />
                    <span
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: GRADIENTS.primary,
                            width: '40px',
                            height: "100%",
                            position: 'absolute',
                            right: '-13px',
                            top: '0',
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: 'white',
                            borderRadius: '0 8px 8px 0',
                            zIndex: 1
                        }}
                    >
                        {showPassword ? '🙈' : '👁️'}
                    </span>
                </div>
                <div className="input-focus-line"></div>
                {errors.newPassword && <span className="input-error">{errors.newPassword}</span>}
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <input
                        className={`custom-input ${errors.confirmPassword ? 'error-input' : ''}`}
                        style={{
                            width: '100%',
                            padding: "18px 50px 18px 18px",
                            borderRadius: "8px",
                            border: "none",
                            outline: "none",
                            height: "5vh",
                        }}
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Confirm Password"
                        minLength="6"
                        autoComplete="new-password"
                    />
                    <span
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: GRADIENTS.primary,
                            width: '40px',
                            height: "100%",
                            position: 'absolute',
                            right: '-13px',
                            top: '0',
                            cursor: 'pointer',
                            fontSize: '18px',
                            color: 'white',
                            borderRadius: '0 8px 8px 0',
                            zIndex: 1
                        }}
                    >
                        {showConfirmPassword ? '🙈' : '👁️'}
                    </span>
                </div>
                <div className="input-focus-line"></div>
                {errors.confirmPassword && <span className="input-error">{errors.confirmPassword}</span>}
            </div>

            <button type="submit" className="login-button" disabled={loading}>
                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <LoadingSpinner size="small" color="white" />
                        <span>Resetting...</span>
                    </div>
                ) : (
                    <span className="button-text">Reset Password</span>
                )}
            </button>
        </form>
    );

    const getHeaderText = () => {
        switch (step) {
            case 1: return { title: "Forgot Password", subtitle: "Enter your email to receive OTP" };
            case 2: return { title: "Verify OTP", subtitle: `Enter the OTP sent to ${formData.email}` };
            case 3: return { title: "Reset Password", subtitle: "Create your new password" };
            default: return { title: "Forgot Password", subtitle: "Enter your email to receive OTP" };
        }
    };

    const headerText = getHeaderText();

    return (
        <div
            className="auth-wrapper animated-background"
            style={{
                background: 'linear-gradient(-45deg, #0d7a77, #159F9C, #1ab3af, #20c4c0)',
                backgroundSize: '400% 400%',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <div className="login-container">
                <div className="glass-form form-entrance">
                    <div style={{
                        background: "transparent",
                        marginBottom: "20px",
                        marginTop: "20px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                    }}>
                        <div className="logo-img">
                            <img src="https://api.vendispot231.online/uploads/logo.png" style={{ height: '60px' }} />
                        </div>
                    </div>

                    <div className="login-header">
                        <h2>{headerText.title}</h2>
                        <p>{headerText.subtitle}</p>
                    </div>

                    {step === 1 && renderStep1()}
                    {step === 2 && renderStep2()}
                    {step === 3 && renderStep3()}

                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <button
                            onClick={() => {
                                setStep(1);
                                setOtpSent(false);
                                setFormData(prev => ({ ...prev, otp: '', newPassword: '', confirmPassword: '' }));
                                navigate('/login');
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#159F9C',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                fontSize: '14px'
                            }}
                        >
                            ← Back to Login
                        </button>
                    </div>

                    <div className="copyright">
                        <p className="text-center mt-3 text-white small copyright">
                            © {new Date().getFullYear()} Vendi-spot
                        </p>
                    </div>
                </div>
            </div>
            
            {/* Inline Toast Container */}
            <div style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 10000
            }}>
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        style={{
                            background: toast.type === 'success' ? '#52c41a' : '#ff4d4f',
                            color: 'white',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            minWidth: '300px',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        {toast.message}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ForgotPassword;